import React from 'react'
import {injectIntl} from 'react-intl'
import styled from 'styled-components'
import FormControl from '@mui/material/FormControl'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import Button from '@mui/material/Button'
import Select from '@mui/material/Select'
import InputLabel from '@mui/material/InputLabel'
import MenuItem from '@mui/material/MenuItem'
import TextField from '@mui/material/TextField'
import FormControlLabel from '@mui/material/FormControlLabel'
import Checkbox from '@mui/material/Checkbox'
import Autocomplete from 'components/Autocomplete'
import PublicFields from 'components/PublicFields'
import {getOwners, getViewsByEntity, getPublicTableSubstitutions, getPublicTableFields} from 'utils/utils'
import {fieldtype} from 'utils/enums/fieldtype'
import {options} from 'utils/enums/options'

const settings = {
    readonly: false,
    now: false,
    autocomplete: null
}

const Row = styled.div`
    display: flex;
    align-items: center;
    margin-bottom: 20px;

	> {
		width: 33.3%;
	}
`

const Control = styled(FormControl)`
	margin-bottom: 20px;
	width: 100%;
`

const Label = styled(FormControlLabel)`
	margin-bottom: 10px;
	display: block;
`

class PublicTable extends React.Component {
    constructor(props) {
        super(props)

        this.owners = getOwners(props.entity)

        if (this.owners.length) {
            const owner = props.table ? this.owners.find(owner => owner.id === props.table.ownerFieldId) ?
                props.table.ownerFieldId : '' : this.owners[0].id
            this.views = getViewsByEntity(props.entity)
            this.substitutions = getPublicTableSubstitutions(props.entity, owner)
            this.fields = getPublicTableFields(props.entity, owner)

            this.state = {
                name: props.table ? props.table.name : '',
                owner,
                viewId: props.table ? props.table.viewId : null,
                naming: props.table && props.table.naming && props.table.naming.length > 0 ? props.table.naming : null,
                create: props.table ? props.table.permissions.create : true,
                delete: props.table ? props.table.permissions.delete : true,
                update: props.table ? props.table.permissions.update : true,
                fields: [],
                order: []
            }
        }
    }

    componentDidMount() {
        if (this.owners.length && this.props.table) {
            const fields = []

            this.props.table.fields.forEach(item => {
                const el = this.fields.find(obj => obj.id === item.fieldId)
                if (el) {
                    const field = {...item}
                    fields.push(field)
                    if (el.uiType === fieldtype.linkedfield) {
                        this.props.fetchLinkedOptions(item.fieldId)
                    }
                }
            })

            this.setState({
                fields,
                order: fields.map(field => field.fieldId)
            })
        }
    }

    handleChange = (e) => {
        if (e.target.name === 'owner') {
            this.substitutions = getPublicTableSubstitutions(this.props.entity, e.target.value)

            this.setState({
                owner: e.target.value
            })
        } else {
            this.setState({
                [e.target.name]: e.target.value
            })
        }
    }

    handleToggle = (e) => {
        if (e.target.name === 'viewId') {
            if (e.target.checked) {
                this.setState({
                    viewId: this.props.table && this.props.table.viewId ? this.props.table.viewId : this.views[0].id
                })
            } else {
                this.setState({
                    viewId: null
                })
            }
        } else if (e.target.name === 'naming') {
            if (e.target.checked) {
                this.setState({
                    naming: ''
                })
            } else {
                this.setState({
                    naming: null
                })
            }
        } else {
            this.setState({
                [e.target.name]: e.target.checked
            })
        }
    }

    handleCreate = () => {
        this.props.createPublicTable({
            entityId: this.props.entity,
            name: this.state.name,
            ownerFieldId: this.state.owner,
            naming: this.state.naming === null ? '' : this.state.naming,
            viewId: this.state.viewId,
            fields: this.state.fields,
            permissions: {
                create: this.state.create,
                update: this.state.update,
                delete: this.state.delete
            }
        })
        this.props.handleClose()
    }

    handleUpdate = () => {
        this.props.updatePublicTable({
            id: this.props.table.id,
            entityId: this.props.entity,
            name: this.state.name,
            ownerFieldId: this.state.owner,
            naming: this.state.naming === null ? '' : this.state.naming,
            viewId: this.state.viewId,
            fields: this.state.fields,
            permissions: {
                create: this.state.create,
                update: this.state.update,
                delete: this.state.delete
            }
        })
        this.props.handleClose()
    }

    handleDelete = () => {
        this.props.deletePublicTable(this.props.table.id)
        this.props.handleClose()
    }

    updateField = (value, key, id) => {
        const fields = [...this.state.fields]
        const field = {...fields.find(item => item.fieldId === id)}
        const index = fields.findIndex(item => item.fieldId === id)

        if (settings[key] !== undefined) {
            Object.keys(settings).forEach(item => {
                field[item] = settings[item]
            })
        }

        field[key] = value
        fields.splice(index, 1, field)

        this.setState({ fields })
    }

    handleSelect = (data) => {
        const fields = [...this.state.fields]

        fields.push({
            fieldId: data.id,
            label: '',
            readonly: false,
            autocomplete: null,
            now: false
        })

        this.setState({
            fields,
            order: fields.map(field => field.fieldId)
        }, () => setTimeout(() => document.querySelector(`[data-field='${data.id}']`).select(), 200))
    }

    deleteField = (id) => {
        const fields = this.state.fields.filter(item => item.fieldId !== id)
        this.setState({
            fields,
            order: fields.map(field => field.fieldId)
        })
    }

    onReorder = (fields, order) => {
        this.setState({
            order,
            fields
        })
    }

    render() {
        const {intl} = this.props
        const validation = this.owners.length ? !this.state.owner || !this.state.name || this.state.name.replace(/\s/g, '') === '' ||
            !this.state.fields.length || this.state.fields.find(item =>
                item.autocomplete === true || (Array.isArray(item.autocomplete) && !item.autocomplete.length)) : false

        return (
            <React.Fragment>
                <DialogTitle>
                    {this.props.table ?
                        intl.formatMessage({id: "sidebar.update_public_table"}) :
                        intl.formatMessage({id: "sidebar.create_public_table"})
                    }
                </DialogTitle>
                <DialogContent>
                {this.owners.length ?
                    <React.Fragment>
                        <Autocomplete
                            style={{marginBottom: '20px'}}
                            value={this.state.name}
                            label={intl.formatMessage({id: "sidebar.public_table_name"})}
                            onChange={(value) => this.setState({name: value})}
                            options={this.substitutions.map(item => item.name)}
                            component='input'
                        />
                        <Control variant="standard">
                            <InputLabel shrink={true}>
                                {intl.formatMessage({id: "sidebar.provide_access_by"})}
                            </InputLabel>
                            <Select
                                value={this.state.owner}
                                onChange={this.handleChange}
                                inputProps={{
                                    name: 'owner'
                                }}
                            >
                                {this.owners.map(item =>
                                    <MenuItem key={item.id} value={item.id}>{item.name}</MenuItem>
                                )}
                            </Select>
                        </Control>
                        {!!this.views.length &&
                            <React.Fragment>
                                <Label
                                    control={
                                        <Checkbox
                                            checked={!!this.state.viewId}
                                            onChange={this.handleToggle}
                                            inputProps={{
                                                name: 'viewId'
                                            }}
                                        />
                                    }
                                    label={intl.formatMessage({id: "sidebar.filter_data"})}
                                />
                                {this.state.viewId &&
                                    <Control variant="standard">
                                        <InputLabel>{intl.formatMessage({id: "sidebar.select_view"})}</InputLabel>
                                        <Select
                                            value={this.state.viewId}
                                            onChange={this.handleChange}
                                            inputProps={{
                                                name: 'viewId'
                                            }}
                                        >
                                            {this.views.map(item =>
                                                <MenuItem key={item.id} value={item.id}>{item.name}</MenuItem>
                                            )}
                                        </Select>
                                    </Control>
                                }
                            </React.Fragment>
                        }
                        <Label
                            control={
                                <Checkbox
                                    checked={this.state.naming !== null}
                                    onChange={this.handleToggle}
                                    inputProps={{
                                        name: 'naming'
                                    }}
                                />
                            }
                            label={intl.formatMessage({id: "sidebar.use_special_naming"})}
                        />
                        {this.state.naming !== null &&
                            <TextField variant="standard"
                                sx={{width: '100%', marginBottom: '20px'}}
                                value={this.state.naming}
                                onChange={this.handleChange}
                                label={intl.formatMessage({id: "sidebar.enter_name"})}
                                InputLabelProps={{
                                    shrink: true
                                }}
                                inputProps={{
                                    maxLength: global.NAME_MAX_LENGTH,
                                    name: 'naming'
                                }}
                            />
                        }
                        <Row>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={this.state.create}
                                        onChange={this.handleToggle}
                                        inputProps={{
                                            name: 'create'
                                        }}
                                    />
                                }
                                label={intl.formatMessage({id: "sidebar.allow_create"})}
                            />
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={this.state.update}
                                        onChange={this.handleToggle}
                                        inputProps={{
                                            name: 'update'
                                        }}
                                    />
                                }
                                label={intl.formatMessage({id: "sidebar.allow_edit"})}
                            />
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={this.state.delete}
                                        onChange={this.handleToggle}
                                        inputProps={{
                                            name: 'delete'
                                        }}
                                    />
                                }
                                label={intl.formatMessage({id: "sidebar.allow_delete"})}
                            />
                        </Row>
                        <PublicFields
                            data={this.fields}
                            list={this.state.fields}
                            handleChange={this.updateField}
                            deleteField={this.deleteField}
                            handleSelect={this.handleSelect}
                            values={this.props.linkedOptions}
                            onReorder={this.onReorder}
                            order={this.state.order}
                            options={options.table}
                        />
                    </React.Fragment>
                    :
                    <div>{intl.formatMessage({id: "sidebar.public_table_warning"})}</div>
                }
                </DialogContent>
                <DialogActions>
                    <Button onClick={this.props.handleClose} color="primary">
                        {intl.formatMessage({id: "app.confirm_cancel"})}
                    </Button>
                    {!!this.owners.length &&
                        <React.Fragment>
                            {this.props.table &&
                                <Button variant="contained"
                                    onClick={this.handleDelete}
                                    color="secondary"
                                >
                                    {intl.formatMessage({id: "app.confirm_delete"})}
                                </Button>
                            }
                            {this.props.table ?
                                <Button variant="contained"
                                    disabled={!!validation}
                                    onClick={this.handleUpdate}
                                    color="primary"
                                >
                                    {intl.formatMessage({id: "app.confirm_update"})}
                                </Button> :
                                <Button variant="contained"
                                    disabled={!!validation}
                                    onClick={this.handleCreate}
                                    color="primary"
                                >
                                    {intl.formatMessage({id: "app.confirm_save"})}
                                </Button>
                            }
                        </React.Fragment>
                    }
                </DialogActions>
            </React.Fragment>
        )
    }
}

export default injectIntl(PublicTable)
